import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

// Chrome/Edge/Samsung fire this when the site can be installed. Not in the DOM typings yet.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "fleetrify_install_prompt_dismissed_at";
const HIDE_FOR_MS = 7 * 24 * 60 * 60 * 1000; // "Not now" hides it for a week

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  (window.navigator as any).standalone === true;

// iPadOS reports itself as a Mac, so also check for a touch screen.
const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const isMobile = () => isIOS() || /android|mobile/i.test(navigator.userAgent);

const recentlyDismissed = () => {
  try {
    const at = Number(localStorage.getItem(DISMISSED_KEY));
    return !!at && Date.now() - at < HIDE_FOR_MS;
  } catch {
    return false;
  }
};

const ShareIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="Share" role="img" className="inline-block align-text-bottom">
    <path d="M12 3v12" />
    <path d="m7 8 5-5 5 5" />
    <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
  </svg>
);

/**
 * Invites phone and tablet users to install Fleetrify as an app.
 * - Android: uses the browser's own install dialog behind an "Install" button.
 * - iPhone/iPad: Apple doesn't let sites trigger installs, so it explains
 *   Share → Add to Home Screen instead.
 * Hidden once installed, when already opened as the app, and for a week after "Not now".
 */
const InstallAppPrompt: React.FC = () => {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [mode, setMode] = useState<"android" | "ios" | null>(null);

  useEffect(() => {
    if (isStandalone() || !isMobile() || recentlyDismissed()) return;

    if (isIOS()) {
      // Give the page a moment to settle before sliding in.
      const timer = setTimeout(() => setMode("ios"), 2500);
      return () => clearTimeout(timer);
    }

    const onBeforeInstall = (event: Event) => {
      event.preventDefault(); // show our own button instead of the mini-infobar
      setInstallEvent(event as BeforeInstallPromptEvent);
      setMode("android");
    };
    // Caught by the script in public/index.html if it fired before the app loaded.
    const early = (window as any).__fleetrifyInstallEvent as BeforeInstallPromptEvent | undefined;
    if (early) onBeforeInstall(early);
    const onInstalled = () => {
      (window as any).__fleetrifyInstallEvent = undefined;
      setMode(null);
      setInstallEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // Storage blocked: it just shows again next visit.
    }
    setMode(null);
  };

  const install = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    // The event can only be used once either way.
    setInstallEvent(null);
    (window as any).__fleetrifyInstallEvent = undefined;
    if (outcome === "accepted") setMode(null);
    else dismiss();
  };

  if (!mode) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="install-app-title"
      className="fixed inset-x-0 bottom-0 z-[10040] px-3 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))]"
    >
      <div className="mx-auto flex max-w-md items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl">
        <img src={`${process.env.PUBLIC_URL}/icon-192.png`} alt="" className="h-12 w-12 flex-shrink-0 rounded-xl border border-gray-100" />
        <div className="min-w-0 flex-1">
          <p id="install-app-title" className="font-semibold text-[#00032A]">
            Install the Fleetrify app
          </p>
          {mode === "android" ? (
            <>
              <p className="mt-0.5 text-sm text-gray-600">
                Open Fleetrify from your home screen, full screen, like any other app.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={install}
                  className="rounded-lg bg-[#143887] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0f2d6b]"
                >
                  Install
                </button>
                <button
                  type="button"
                  onClick={dismiss}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                >
                  Not now
                </button>
              </div>
            </>
          ) : (
            <>
              <ol className="mt-1 space-y-1 text-sm text-gray-600">
                <li>
                  1. Tap the Share button <ShareIcon /> in your browser.
                </li>
                <li>
                  2. Choose <span className="font-semibold text-[#00032A]">Add to Home Screen</span>.
                </li>
              </ol>
              <p className="mt-2 text-xs text-gray-400">Don't see it? Open this page in Safari.</p>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="-m-1 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default InstallAppPrompt;
